import React from 'react';
import { View, Text, type ViewStyle } from 'react-native';
import { Pressable } from 'react-native';
import { cn } from '@/lib/cn';
import { useThemeColors } from '@/lib/useThemeColors';

interface CardHeaderProps {
  title: string;
  subtitle?: string;
  right?: React.ReactNode;
}

interface CardProps {
  children: React.ReactNode;
  className?: string;
  /** Optional press handler. When provided, the card becomes a Pressable. */
  onPress?: () => void;
  /** Optional long press handler */
  onLongPress?: () => void;
  /** Card header with title, optional subtitle and right-side content */
  header?: CardHeaderProps;
  /** Whether to show the default shadow (default: false) */
  elevated?: boolean;
  /** Override border width (default: 1) */
  borderWidth?: number;
  /** Override border color */
  borderColor?: string;
  /** Override background color */
  backgroundColor?: string;
  /** Additional inline styles */
  style?: ViewStyle;
  /** Accessibility label for the card */
  accessibilityLabel?: string;
}

/**
 * Reusable card container with consistent rounded corners, border, and background.
 * Extracted from the most common card pattern across dealflow and portfolio screens.
 *
 * Usage:
 * ```tsx
 * <Card header={{ title: "My Section", right: <Badge /> }}>
 *   <Text>Content here</Text>
 * </Card>
 * ```
 */
export function Card({
  children,
  className,
  onPress,
  onLongPress,
  header,
  elevated = false,
  borderWidth = 1,
  borderColor,
  backgroundColor,
  style,
  accessibilityLabel,
}: CardProps) {
  const colors = useThemeColors();

  const cardStyle: ViewStyle = {
    backgroundColor: backgroundColor ?? colors.surface.primary,
    borderWidth,
    borderColor: borderColor ?? colors.border.light,
    ...(elevated && {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.08,
      shadowRadius: 4,
      elevation: 3,
    }),
    ...style,
  };

  const content = (
    <>
      {header && (
        <View className="flex-row items-center justify-between mb-3">
          <View className="flex-1">
            <Text
              style={{ color: colors.text.primary }}
              className="font-semibold text-base"
            >
              {header.title}
            </Text>
            {header.subtitle && (
              <Text
                style={{ color: colors.text.secondary }}
                className="text-sm mt-0.5"
              >
                {header.subtitle}
              </Text>
            )}
          </View>
          {header.right && <View>{header.right}</View>}
        </View>
      )}
      {children}
    </>
  );

  if (onPress || onLongPress) {
    return (
      <Pressable
        onPress={onPress}
        onLongPress={onLongPress}
        className={cn('rounded-xl overflow-hidden p-4', className)}
        style={cardStyle}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
      >
        {content}
      </Pressable>
    );
  }

  return (
    <View
      className={cn('rounded-xl overflow-hidden p-4', className)}
      style={cardStyle}
      accessibilityLabel={accessibilityLabel}
    >
      {content}
    </View>
  );
}
